import { useParams, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';

interface AddonDetail {
  id: string;
  icon: string;
  name: string;
  subtitle: string;
  price: string;
  priceNote: string;
  heroImage: string;
  description: string;
  features: string[];
  whoThisIsFor: {
    title: string;
    description: string;
    idealFor: string[];
  };
  whatToExpect: {
    title: string;
    description: string;
    breakdown: Array<{
      phase: string;
      duration: string;
      focus: string[];
    }>;
  };
  additionalBenefits: string[];
}

const addonData: Record<string, AddonDetail> = {
  'one-on-one-strike': {
    id: 'one-on-one-strike',
    icon: 'ri-speed-up-line',
    name: '1 on 1 Strike Session',
    subtitle: 'Individual Performance Session',
    price: 'Contact for Pricing',
    priceNote: 'Flexible scheduling available',
    heroImage: 'https://public.readdy.ai/ai/img_res/b8052c51ada3259159324715c83048b2.jpg',
    description: 'In this one-on-one session you will get everything in the assessment, along with a personalized training guide to help you get started in the right direction. No more guesswork — just a clear, data-backed roadmap built specifically for you.',
    features: [
      'Full individual assessment included in session',
      'Velocity measurement with Rapsodo / TrackMan',
      'Complete biomechanical video analysis of throwing mechanics',
      'Arm health and stress evaluation',
      'Ground force production measurement',
      'Personalized training guide delivered after session',
      'Detailed written report with all metrics and video links',
      'Recommended drill progressions and corrective exercises',
      'One follow-up check-in call within 30 days',
      'Comparison to age-appropriate peer benchmarks',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Designed for athletes who want focused, individual attention and a clear starting point without committing to a full program.',
      idealFor: [
        'Athletes who want a complete picture of their current performance in one session',
        'Players preparing for tryouts, showcases, or combines who need current metrics',
        'Athletes unsure where to start and looking for a personalized roadmap',
        'Players who have hit a plateau and need expert eyes on their mechanics',
        'Parents who want objective, data-driven feedback on their athlete\'s development',
        'Athletes returning from injury who need a full movement and arm health check',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'A focused, high-value individual session that combines a full assessment with actionable coaching and a take-home training guide.',
      breakdown: [
        {
          phase: 'Intake & Goal Setting',
          duration: '60 minutes',
          focus: [
            'Review athletic history and any injury background',
            'Discuss current goals and training experience',
            'Establish session priorities and expectations',
          ],
        },
        {
          phase: 'Full Assessment',
          duration: '2 hours',
          focus: [
            'Movement screen and biomechanical analysis',
            'Velocity measurement and arm health evaluation',
            'Video capture of throwing mechanics from multiple angles',
            'Ground force production for pitching and hitting',
            'Spin rate and ball flight metrics',
          ],
        },
        {
          phase: 'Data Review & Coaching',
          duration: '60 minutes',
          focus: [
            'Live review of all collected metrics',
            'Video breakdown of mechanics with coach feedback',
            'Identification of top 2-3 priority areas for improvement',
            'Comparison to peer benchmarks and age norms',
          ],
        },
        {
          phase: 'Training Guide Delivery',
          duration: 'Within 1 week',
          focus: [
            'Personalized drill progressions emailed to athlete',
            'Corrective exercise recommendations',
            'Arm care protocol tailored to findings',
            'Clear 4-week starter plan to implement immediately',
          ],
        },
      ],
    },
    additionalBenefits: [
      'All data and video links emailed directly to athlete',
      'Training guide formatted for easy daily use',
      'One follow-up consultation call within 30 days',
      'Assessment fee credited toward any full program enrollment',
      'Access to facility resources on session day',
      'Coach available for questions via email after session',
    ],
  },
  consultant: {
    id: 'consultant',
    icon: 'ri-brain-line',
    name: 'Consultant Services',
    subtitle: 'Player Development Consulting',
    price: 'First 30 Min Free',
    priceNote: 'Custom rates after initial call',
    heroImage: 'https://readdy.ai/api/search-image?query=Professional%20sports%20consultant%20meeting%20with%20baseball%20coaching%20staff%20in%20modern%20conference%20room%2C%20whiteboard%20with%20player%20development%20diagrams%20and%20analytics%20charts%2C%20serious%20professional%20atmosphere%20with%20dark%20walls%20and%20warm%20accent%20lighting%2C%20coaches%20reviewing%20data%20on%20tablets%20and%20laptops%2C%20premium%20sports%20organization%20environment%20with%20teal%20and%20dark%20color%20scheme&width=1920&height=600&seq=addonconsultant001&orientation=landscape',
    description: "Looking to find answers with your team's player development? Give us a call — the first 30 minutes are free. Whether you're a coach, organization, or parent, we bring data-driven insight and proven methodology to help you build better athletes.",
    features: [
      'First 30-minute consultation call at no charge',
      'Player development program design and review',
      'Pitching staff evaluation and arm care protocols',
      'Organizational assessment and training structure audit',
      'Data interpretation and metric benchmarking',
      'Recruiting strategy and athlete positioning guidance',
      'Custom curriculum development for teams or academies',
      'On-site evaluation visits available upon request',
      'Ongoing advisory retainer options available',
      'Written summary report after each consultation',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Built for coaches, organizations, and parents who want expert guidance on player development without the guesswork.',
      idealFor: [
        'High school and college coaching staffs looking to modernize their player development approach',
        'Travel ball organizations wanting to build a structured development curriculum',
        'Parents navigating the recruiting process and needing expert direction',
        'Private academies looking to audit and improve their training programs',
        'Athletic directors evaluating program effectiveness and athlete outcomes',
        'Individual coaches seeking mentorship and professional development',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'A professional, results-oriented consulting engagement tailored to your specific needs and organizational goals.',
      breakdown: [
        {
          phase: 'Initial Discovery Call',
          duration: '30 minutes — Free',
          focus: [
            'Understand your current program structure and challenges',
            'Identify key pain points and development gaps',
            'Discuss goals and desired outcomes',
            'Determine scope of consulting engagement',
          ],
        },
        {
          phase: 'Assessment & Analysis',
          duration: 'Varies by scope',
          focus: [
            'Review existing training data and athlete metrics',
            'Evaluate current programming and methodology',
            'Benchmark against industry standards and best practices',
            'Identify highest-leverage areas for improvement',
          ],
        },
        {
          phase: 'Recommendations & Deliverables',
          duration: 'Within agreed timeline',
          focus: [
            'Detailed written report with findings and recommendations',
            'Prioritized action plan with implementation steps',
            'Custom curriculum or protocol design if applicable',
            'Presentation to staff or stakeholders if needed',
          ],
        },
        {
          phase: 'Ongoing Support',
          duration: 'Optional retainer',
          focus: [
            'Regular check-in calls to monitor implementation',
            'Adjustments to programming based on results',
            'Access to coach for questions and guidance',
            'Quarterly review and program refinement',
          ],
        },
      ],
    },
    additionalBenefits: [
      'No obligation after the free initial call',
      'Flexible engagement formats — single call, project-based, or retainer',
      'Confidential and professional handling of all organizational data',
      'Access to our network of sports science and performance specialists',
      'Written summary provided after every paid consultation session',
      'Discounted rates for ongoing or multi-session engagements',
    ],
  },
  'remote-video': {
    id: 'remote-video',
    icon: 'ri-video-line',
    name: 'Remote Video Analysis',
    subtitle: 'Biomechanical Feedback — Anywhere',
    price: 'Contact for Pricing',
    priceNote: 'Per submission or bundle packages available',
    heroImage: 'https://readdy.ai/api/search-image?query=Baseball%20coach%20analyzing%20slow%20motion%20pitching%20video%20on%20large%20monitor%20with%20biomechanical%20overlay%20markers%20and%20angle%20measurements%2C%20dark%20professional%20sports%20analysis%20room%20with%20multiple%20screens%20showing%20athlete%20footage%20from%20different%20angles%2C%20teal%20accent%20lighting%20on%20dark%20walls%2C%20detailed%20frame%20by%20frame%20analysis%20interface%20visible%20on%20screen%2C%20modern%20sports%20technology%20environment&width=1920&height=600&seq=addonremotevideo001&orientation=landscape',
    description: 'Submit video from anywhere and receive detailed biomechanical feedback from our coaching staff. Whether you\'re across the country or in the off-season, our remote video analysis keeps your development moving forward with expert eyes on your mechanics.',
    features: [
      'Submit video from any location via secure upload link',
      'Frame-by-frame biomechanical breakdown by certified coaches',
      'Multi-angle analysis with annotated video returned',
      'Written feedback report with key findings and priorities',
      'Drill and corrective exercise recommendations included',
      'Comparison to previous submissions to track progress',
      'Pitching, hitting, and fielding mechanics covered',
      'Turnaround time of 48-72 hours per submission',
      'Video library of recommended drills provided',
      'Follow-up Q&A via email or call available',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Perfect for athletes who want professional coaching feedback without geographic limitations or scheduling constraints.',
      idealFor: [
        'Athletes who live outside the local area but want access to elite coaching',
        'In-season players who need quick mechanical feedback between games',
        'Athletes working with a local coach who want a second expert opinion',
        'College players looking for off-season development support',
        'Travel ball athletes who train in multiple locations throughout the year',
        'Any athlete who wants consistent, professional eyes on their mechanics',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'A streamlined, professional remote coaching experience that delivers expert feedback directly to you — no matter where you are.',
      breakdown: [
        {
          phase: 'Video Submission',
          duration: 'Your schedule',
          focus: [
            'Receive secure invite to our CoachNow platform.',
            'Submit video files from phone, camera, or any device',
            'Include brief notes on what you want analyzed',
            'Angles of video, depend on what we are analyzing, be sure to contact us for proper video',
          ],
        },
        {
          phase: 'Coach Analysis',
          duration: '48-72 hours',
          focus: [
            'Frame-by-frame review by certified coaching staff',
            'Biomechanical markers and angle measurements applied',
            'Identification of mechanical strengths and areas for improvement',
          ],
        },
        {
          phase: 'Feedback Delivery',
          duration: 'Within 72 hours',
          focus: [
            'Annotated video returned with coach voiceover or text overlays',
            'Report summarizing key findings',
            'Prioritized list of 2-3 focus areas',
          ],
        },
        {
          phase: 'Follow-Up',
          duration: 'Optional',
          focus: [
            'Email Q&A to clarify any feedback points',
            'Optional 15-minute call to discuss findings in depth',
            'Progress tracking if submitting multiple videos over time',
          ],
        },
      ],
    },
    additionalBenefits: [
      'No travel required — train anywhere, get elite feedback',
      'Annotated video files yours to keep and reference',
      'Bundle packages available for cost savings on multiple submissions',
      'Progress comparison reports when submitting regularly',
      'Feedback compatible with any existing training program',
    ],
  },
  'team-group': {
    id: 'team-group',
    icon: 'ri-group-line',
    name: 'Team / Group Packages',
    subtitle: 'Custom Team & Organization Pricing',
    price: 'Custom Pricing',
    priceNote: 'Contact us for a tailored quote',
    heroImage: 'https://readdy.ai/api/search-image?query=Baseball%20team%20training%20session%20in%20large%20professional%20facility%20with%20multiple%20athletes%20doing%20agility%20drills%20simultaneously%2C%20coaches%20supervising%20group%20workout%20with%20modern%20equipment%2C%20energetic%20team%20atmosphere%20with%20dark%20walls%20and%20bright%20overhead%20lighting%2C%20organized%20group%20training%20with%20cones%20and%20resistance%20bands%2C%20premium%20sports%20performance%20facility%20with%20teal%20accent%20lighting&width=1920&height=600&seq=addonteamgroup001&orientation=landscape',
    description: 'Custom pricing for teams, travel ball organizations, and school programs. We build fully tailored training packages that fit your roster size, schedule, and development goals — delivering elite-level coaching at a group rate that makes sense for your organization.',
    features: [
      'Custom program design for your specific team or organization',
      'Group assessment sessions for full roster baseline testing',
      'Individualized programming within the group structure',
      'Flexible scheduling to work around your game and practice calendar',
      'Dedicated coach assigned to your team for the program duration',
      'Group and individual metric tracking and reporting',
      'Arm care and injury prevention protocols for full roster',
      'Mental performance workshops for team cohesion and mindset',
      'Speed, agility, and strength training for all positions',
      'End-of-program performance report for each athlete',
      'Parent and coaching staff communication throughout',
      'On-site visits to your facility available upon request',
    ],
    whoThisIsFor: {
      title: 'Who This Is For',
      description: 'Designed for organizations that want to elevate their entire roster with structured, data-driven development at a group rate.',
      idealFor: [
        'Travel ball organizations looking to give their players a competitive edge',
        'High school programs wanting to modernize their off-season training',
        'College programs seeking supplemental performance coaching',
        'Youth leagues and academies building a development curriculum',
        'Private baseball academies looking to partner with a performance specialist',
        'Any organization with 5+ athletes who want elite-level group training',
      ],
    },
    whatToExpect: {
      title: 'What to Expect',
      description: 'A fully customized team training experience built around your roster, schedule, and organizational goals.',
      breakdown: [
        {
          phase: 'Discovery & Needs Assessment',
          duration: '1 week',
          focus: [
            'Initial call with coaching staff or organization leadership',
            'Review of current training structure and athlete profiles',
            'Identify team-wide development priorities',
            'Discuss scheduling, budget, and logistical requirements',
          ],
        },
        {
          phase: 'Group Assessment Sessions',
          duration: '1-2 days',
          focus: [
            'Full roster baseline testing and movement screens',
            'Velocity and arm health evaluation for all pitchers',
            'Athletic testing for all position players',
            'Individual metric reports generated for each athlete',
          ],
        },
        {
          phase: 'Program Delivery',
          duration: 'Custom duration',
          focus: [
            'Structured group training sessions per agreed schedule',
            'Individual programming within group sessions',
            'Weekly progress check-ins with coaching staff',
            'Ongoing metric tracking and program adjustments',
          ],
        },
        {
          phase: 'End-of-Program Review',
          duration: '1 session',
          focus: [
            'Final assessment and benchmark comparison for all athletes',
            'Individual performance reports for each player',
            'Team-wide summary report for coaching staff',
            'Recommendations for continued development',
          ],
        },
      ],
    },
    additionalBenefits: [
      'Volume discounts for larger rosters',
      'Flexible payment structures for organizations and school programs',
      'On-site training at your facility available for qualifying groups',
      'Individual athlete reports shareable with parents and recruiters',
      'Priority scheduling for team sessions',
      'Option to continue with individual programs after group package ends',
    ],
  },
};

export default function AddonDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const addon = id ? addonData[id] : null;

  useEffect(() => {
    if (!addon) return;
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-addon-detail';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": `${addon.name} | Elite Performance Lab`,
      "description": addon.description,
      "url": `${import.meta.env.VITE_SITE_URL}/addon/${addon.id}`,
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Pricing", "item": `${import.meta.env.VITE_SITE_URL}/pricing` },
          { "@type": "ListItem", "position": 3, "name": addon.name, "item": `${import.meta.env.VITE_SITE_URL}/addon/${addon.id}` }
        ]
      },
      "mainEntity": {
        "@type": "Service",
        "name": addon.name,
        "description": addon.description,
        "provider": {
          "@type": "Organization",
          "name": "Elite Performance Lab",
          "address": { "@type": "PostalAddress", "addressLocality": "Corsicana", "addressRegion": "TX", "addressCountry": "US" }
        },
        "offers": {
          "@type": "Offer",
          "description": addon.priceNote
        }
      }
    });
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-addon-detail');
      if (el) el.remove();
    };
  }, [addon]);

  if (!addon) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-black text-white mb-4">Service Not Found</h1>
          <button
            onClick={() => navigate('/pricing')}
            className="px-8 py-3 bg-teal-500 text-white font-semibold rounded-xl hover:bg-teal-400 transition-all cursor-pointer whitespace-nowrap"
          >
            Back to Pricing
          </button>
        </div>
      </div>
    );
  }

  const goToContact = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Navigation />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img
          src={addon.heroImage}
          alt={addon.name}
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-gray-900"></div>
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <button
            onClick={() => navigate('/pricing')}
            className="flex items-center gap-2 text-teal-400 hover:text-teal-300 transition-colors mb-8 cursor-pointer group"
          >
            <i className="ri-arrow-left-line text-xl group-hover:-translate-x-1 transition-transform"></i>
            <span className="font-semibold">Back to Pricing</span>
          </button>

          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500/20 rounded-2xl mb-6 border border-teal-500/30">
              <i className={`${addon.icon} text-3xl text-teal-400`}></i>
            </div>
            <p className="text-teal-400 text-sm font-semibold tracking-widest uppercase mb-4">
              {addon.subtitle}
            </p>
            <h1 className="text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              {addon.name}
            </h1>
            <div className="mb-6">
              <div className="text-3xl font-black text-teal-400 mb-1">{addon.price}</div>
              <p className="text-gray-400 text-sm">{addon.priceNote}</p>
            </div>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {addon.description}
            </p>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">What's Included</h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {addon.features.map((feature, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-800 rounded-xl p-5 border border-gray-700">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-checkbox-circle-fill text-teal-400 text-xl"></i>
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              {addon.whoThisIsFor.title}
            </h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
              {addon.whoThisIsFor.description}
            </p>
          </div>

          <div className="bg-gray-900 rounded-2xl p-8 border border-gray-700">
            <h3 className="text-xl font-bold text-white mb-6">Ideal For:</h3>
            <div className="space-y-4">
              {addon.whoThisIsFor.idealFor.map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-user-star-line text-teal-400 text-lg"></i>
                  </div>
                  <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 pt-6 border-t border-gray-700 flex justify-center">
              <button
                onClick={goToContact}
                className="inline-flex items-center gap-3 px-10 py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold text-base rounded-full transition-all shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:scale-105 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-lg"></i>
                Get in Touch
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">
              {addon.whatToExpect.title}
            </h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto mb-6"></div>
            <p className="text-gray-300 text-lg max-w-3xl mx-auto leading-relaxed">
              {addon.whatToExpect.description}
            </p>
          </div>

          <div className="space-y-6">
            {addon.whatToExpect.breakdown.map((phase, i) => (
              <div
                key={i}
                className="bg-gray-800 rounded-2xl p-7 border border-gray-700 hover:border-teal-500/50 transition-all"
              >
                <div className="flex items-start gap-5 mb-5">
                  <div className="w-12 h-12 flex items-center justify-center flex-shrink-0 bg-teal-500/10 rounded-xl">
                    <span className="text-2xl font-black text-teal-400">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{phase.phase}</h3>
                    <p className="text-teal-400 text-sm font-medium">{phase.duration}</p>
                  </div>
                </div>

                <ul className="space-y-3 pl-17">
                  {phase.focus.map((item, fi) => (
                    <li key={fi} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-arrow-right-s-line text-teal-400 text-base"></i>
                      </div>
                      <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-20 bg-gray-800">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-3xl lg:text-4xl font-black text-white mb-4">Additional Benefits</h2>
            <div className="w-20 h-1 bg-teal-500 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {addon.additionalBenefits.map((benefit, i) => (
              <div key={i} className="flex items-start gap-4 bg-gray-900 rounded-xl p-5 border border-gray-700">
                <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className="ri-gift-line text-teal-400 text-lg"></i>
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <button
              onClick={goToContact}
              className="inline-flex items-center gap-3 px-10 py-4 bg-teal-500 hover:bg-teal-400 text-white font-bold text-base rounded-full transition-all shadow-lg shadow-teal-500/25 hover:shadow-teal-400/40 hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-lg"></i>
              Get in Touch
            </button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-teal-700 to-teal-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-black text-white mb-4">Ready to Get Started?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Reach out today and our team will get back to you quickly to discuss how this service fits your goals.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4">
            <button
              onClick={goToContact}
              className="px-10 py-4 bg-white text-teal-700 text-base font-bold rounded-full hover:bg-gray-100 transition-all shadow-2xl cursor-pointer whitespace-nowrap"
            >
              Contact Us Now
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="px-10 py-4 bg-transparent text-white text-base font-bold rounded-full border-2 border-white hover:bg-white hover:text-teal-700 transition-all cursor-pointer whitespace-nowrap"
            >
              View All Services
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
